import { doc, getDocFromServer, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { normalizarMetasPessoa } from '@/metas/calculo';
import { ouvirVigiado } from '@/lib/listenerVigiado';

/**
 * `metasPorSdr` limpo: por pessoa, os quatro números (V5). Lixo e formato antigo
 * (`{ valor }`) viram o objeto completo; pessoa sem nenhuma meta é descartada.
 */
export function normalizarMetasPorSdr(metasPorSdr) {
  const saida = {};
  for (const [uid, m] of Object.entries(metasPorSdr ?? {})) {
    if (!uid) continue;
    const n = normalizarMetasPessoa(m);
    if (Object.values(n).some((v) => v > 0)) saida[uid] = n;
  }
  return saida;
}

/** Metas ficam por mês: clinicas/{id}/metas/{'YYYY-MM'}. */
const refMetas = (clinicaId, mes) => doc(db, 'clinicas', clinicaId, 'metas', mes);

const dadosOuNulo = (snap) => (snap.exists() ? snap.data() : null);

/**
 * ★★★ WU-2/P3+P7 — UM documento por mês, e ele manda no ritmo de todo mundo.
 *
 * ⚠⚠⚠ Este é o documento do caso de 16/out: `metasPorSdr[uid].ligacoesDia` estava gravado no
 * servidor desde **14/10 11:50:41Z** e a tela da SDR dizia *"Sem meta por dia"* **48 h
 * depois** (`docs/wu-reincidencia-1610.md` §3.1). ★ Por `ouvirVigiado`, a primeira emissão
 * vinda do cache passa a ser **conferida com o servidor**, e um erro no listener deixa de ser
 * definitivo.
 *
 * ★ `aoDesatualizar(bool)` é opcional: quem não o passa continua funcionando igual — só não
 * mostra a faixa de "pode estar velho".
 */
export function ouvirMetas(clinicaId, mes, aoReceber, aoFalhar, aoDesatualizar) {
  const ref = refMetas(clinicaId, mes);
  return ouvirVigiado({
    assunto: 'as metas',
    // ⚠ `includeMetadataChanges` é obrigatório aqui: sem ele a transição
    // "cache → servidor" não produz emissão, e `fromCache` nunca seria visto virar `false`.
    assinar: (aoSnap, aoErro) => onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => aoSnap(dadosOuNulo(snap), snap.metadata.fromCache),
      aoErro,
    ),
    doServidor: async () => dadosOuNulo(await getDocFromServer(ref)),
    aoReceber,
    aoFalhar,
    aoDesatualizar,
  });
}

const numero = (v) => Number(String(v ?? '').replace(',', '.')) || 0;

/**
 * ★★★ A-7a — OS CAMPOS **GERAIS** DO MÊS, e esta lista é a autoridade.
 *
 * ⚠ `diasUteis` **não está aqui**, de propósito: ele deixou de ser digitado junto com as
 * metas (a grade não o edita) e passa a sair de Configurações na A-7b. ★ Enquanto isso ele
 * fica **intocado** no documento — é o divisor que produção já tem gravado.
 *
 * ★ `metaConversaoValorPct` é campo NOVO (a linha "Conversão de valor" da grade pede um
 * alvo digitável, e não havia nenhum).
 */
export const CAMPOS_GERAIS = [
  'metaAgendamentoPct',
  'metaComparecimentoPct',
  'metaFechamentoPct',
  'metaConversaoValorPct',
  'ticketMedioAlvo',
  'metaValorVendas',
  'investimento',
  'modo',
];

/** Só os campos gerais, normalizados — nunca `metasPorSdr`, nunca `diasUteis`. */
function apenasGerais(metas) {
  return {
    metaAgendamentoPct: numero(metas.metaAgendamentoPct),
    metaComparecimentoPct: numero(metas.metaComparecimentoPct),
    metaFechamentoPct: numero(metas.metaFechamentoPct),
    metaConversaoValorPct: numero(metas.metaConversaoValorPct),
    ticketMedioAlvo: numero(metas.ticketMedioAlvo),
    metaValorVendas: numero(metas.metaValorVendas),
    investimento: numero(metas.investimento),
    modo: metas.modo === 'investimento' ? 'investimento' : 'vendas',
  };
}

/**
 * ★★★ A-7a — A ESCRITA PARTIDA EM DUAS, e é o detalhe que impede perda de dado.
 *
 * ⚠⚠⚠ Até aqui havia **um** gravador (`salvarMetas`), com `setDoc` **sem `merge`**: o
 * documento era inteiramente os campos daquele formulário. ★ Com **dois** editores — a grade
 * do Dashboard (metas gerais) e o diálogo de Indicadores (metas individuais) — manter aquilo
 * significaria: a grade salvando **apaga `metasPorSdr`** de todo mundo, e o diálogo
 * individual salvando **apaga as metas gerais**. Nenhum dos dois avisaria.
 *
 * ★★★ POR QUE `mergeFields`, E NÃO `merge: true` — ERRO DE MEDIÇÃO CORRIGIDO (13/out/2026).
 *
 * ⚠⚠⚠ O plano da peça afirmava: *"`metasPorSdr` é campo de PRIMEIRO nível, então
 * `merge: true` SUBSTITUI o mapa inteiro"*. **É FALSO, e foi medido no emulador:** com
 * `merge: true` o Firestore funde **recursivamente** os mapas aninhados, chave por chave —
 * então salvar um mapa menor **NÃO remove** quem saiu. ★ O caso E5 (*"salvar de novo sem uma
 * pessoa a remove"*) ficou vermelho na primeira execução, com `uid-A` ainda no documento.
 *
 * ★★ `mergeFields: [...]` é a opção que faz o que o plano supunha de `merge`: para cada
 * caminho de campo listado, o valor **substitui** o que havia (é a semântica de `updateDoc`),
 * e todo campo **fora** da lista fica intocado. ⚠ E, diferente de `updateDoc`, ele **cria** o
 * documento quando o mês ainda não existe — que é o primeiro salvamento de todo mês novo.
 *
 * ★ Então a lista de campos deixou de ser documentação e virou MECANISMO: é ela que delimita
 * o que cada função pode tocar.
 */
export async function salvarMetasGerais(clinicaId, mes, metas) {
  await setDoc(
    refMetas(clinicaId, mes),
    { mes, ...apenasGerais(metas) },
    { mergeFields: ['mes', ...CAMPOS_GERAIS] },
  );
}

/**
 * Só o mapa de metas individuais — as gerais e `diasUteis` ficam onde estão.
 *
 * ★ `mergeFields` com `metasPorSdr` SUBSTITUI o mapa inteiro: salvar sem uma pessoa a remove.
 * ⚠⚠ Com `merge: true` ela ficaria (medido — ver o comentário acima), e é a limpeza que o
 * diálogo promete quando o gestor apaga a meta de alguém.
 */
export async function salvarMetasPorSdr(clinicaId, mes, metasPorSdr) {
  await setDoc(
    refMetas(clinicaId, mes),
    { mes, metasPorSdr: normalizarMetasPorSdr(metasPorSdr) },
    { mergeFields: ['mes', 'metasPorSdr'] },
  );
}

/**
 * ⚠⚠ LEGADO — o gravador único do `DialogoMetas`, que não está mais no caminho de ninguém.
 *
 * `/metas` redireciona para `/dashboard` desde a A-7a, então esta função só é alcançada pelos
 * testes da tela antiga. ★ Ela ficou aqui, e **sem `setDoc` cru**, por uma razão: enquanto o
 * arquivo existir, ninguém deve poder chegar nele e encontrar o gravador destrutivo de volta.
 * ⚠ `diasUteis` continua passando por aqui porque o formulário antigo o edita; a grade nova
 * não o toca.
 */
export async function salvarMetas(clinicaId, mes, metas) {
  await setDoc(
    refMetas(clinicaId, mes),
    {
      mes,
      ...apenasGerais(metas),
      diasUteis: Array.isArray(metas.diasUteis) ? metas.diasUteis : [1, 2, 3, 4, 5],
      metasPorSdr: normalizarMetasPorSdr(metas.metasPorSdr),
    },
    { mergeFields: ['mes', ...CAMPOS_GERAIS, 'diasUteis', 'metasPorSdr'] },
  );
}
